import { useCallback, useEffect, useState } from "react";
import StepZero from "./StepZero";
import StepOne from "./StepOne";
import StepTwo from "./StepTwo";
import StepThree from "./StepThree";
import StepFour from "./StepFour";
import StepFive from "./StepFive";
import StepFiveModel from "./StepFiveModel";
import { useParams, useNavigate } from "react-router";
import DashboardLayout from "@/components/layouts/Dashboard";
import Steps from "@/components/ui/Steps";
import Alert from "@/components/ui/Alert/index";
import Card from "@/components/ui/Card/index";
import { apiGetShredProgress } from "@/services/ShredService";
import { ShredResponse } from "@/@types/shred";
import ExplanationBox from "@/components/ExplanationBox.tsx";
import { shredNameExplanation as shredNameHint } from "@/components/explanations/ShredNameExplanation.tsx";
import {
  shredsListExplanation as shredDescriptionHint
} from "@/components/explanations/ShredsDescriptionExplanation.tsx";
import { shredsListExplanation as shredPlanHint } from "@/components/explanations/ShredsPlanExplanation";
import { shredsListExplanation as shredListHint } from "@/components/explanations/ShredsIOExplanation";

console.log("🔍 StepController file imported!");

type ValidationResponse = {
  status: number;
  state: string;
  body: any;
}

const StepController = () => {
  console.log("🔍 StepController component LOADED!");
  
  const { projectId, serviceId, id } = useParams();
  
  const navigate = useNavigate();
  
  const [step, setStep] = useState(0);
  
  const [shredId, setShredId] = useState("");
  const [shredName, setShredName] = useState("");
  const [shredDescription, setShredDescription] = useState("");
  const [shredPlan, setShredPlan] = useState("");
  const [shredStartDate, setStartDate] = useState("");
  const [shredShredProgress, setShredProgress] = useState(0);
  const [shredCoinsUsed, setShredCoinsUsed] = useState("");
  const [shredGenerationTime, setShredGenerationTime] = useState("");
  const [shredResponse, setShredResponse] = useState<ShredResponse>();
  const [isModel, setIsModel] = useState(false);
  const [isModelValidated, setIsModelValidated] = useState(false);
  
  const [errorMessage, setErrorMessage] = useState("");
  
  const [loading, setLoading] = useState(false);
  
  const getShred = useCallback(() => {
    setLoading(true);
    
    apiGetShredProgress(id)
      .then((result) => {
        handleValidationResponse(result.data);
      })
      .catch(() => {
        handleErrorMessage("An error occurred while fetching the shred");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id]);
  
  const handleValidationResponse = (response: ValidationResponse) => {
    console.log("🔍 handleValidationResponse:", response);
    console.log("🔍 response.state:", response.state);
    console.log("🔍 response.body:", response.body);
    
    switch (response.state) {
      case "SHRED_CREATED":
        setShredId(response.body);
        navigate(`/architecture/project/${projectId}/shred/${response.body}`);
        
        setStep(1);
        break;
      case "DESCRIPTION_VALIDATED":
        console.log("🔍 DESCRIPTION_VALIDATED response:", response.body);
        setShredDescription(response.body.description);
        
        setIsModel(response.body.model || false);
        
        if (response.body.model) {
          setStep(1); // Show StepTwo for model validation
          // If the validation was successful, mark as validated
          if (response.body.validated) {
            console.log("🔍 Setting isModelValidated to true for validated model");
            setIsModelValidated(true);
          } else {
            console.log("🔍 Model not validated yet, keeping isModelValidated false");
          }
        } else {
          setStep(2);
        }
        break;
      case "SHRED_MODEL_SAVED":
        console.log("🔍 SHRED_MODEL_SAVED - staying on StepTwo for model validation");
        // For model validation success, stay on StepTwo (step 1) to show the back button
        setStep(1);
        setIsModelValidated(true);
        break;
      case "PLAN_GENERATION":
        setStep(3);
        break;
      case "SHREDS_GENERATION":
        setStep(4);
        break;
      case "FAILED":
        setErrorMessage(response.body.message);
        break;
      case "PROGRESS":
        setShredId(response.body.shredId);
        setShredName(response.body.name);
        setShredDescription(response.body.summary);
        setShredPlan(response.body.htmlPlan);
        setStartDate(response.body.startDate);
        setShredProgress(response.body.progress);
        setShredCoinsUsed(response.body.coinsUsed);
        setShredGenerationTime(response.body.generationTime);
        setShredResponse(response.body);
        setIsModel(response.body.model || false);
        
        console.log("🔍 PROGRESS status:", response.body.status, "isModel:", response.body.model);
        
        switch (response.body.status) {
          case "CREATED":
          case "SPECS_GENERATED":
            setStep(1);
            break;
          case"PROMPT_VALIDATED":
            // For models, go to StepTwo for validation
            if (response.body.model) {
              setStep(1);
            } else {
              setStep(2);
            }
            break;
          case "PLAN_GENERATION":
          case "PLAN_GENERATED":
          case "PLAN_FAILED":
            setStep(3);
            break;
          case "IN_GENERATION":
          case "RESUMING_GENERATION":
          case "FAILED":
          case "STOPPING":
          case "STOPPED":
            setStep(4);
            break;
          case "DONE":
            // For model shreds, show validation interface (StepTwo)
            // For regular shreds, show generation results (StepFive)
            if (response.body.model) {
              setStep(1); // StepTwo for model validation
              setIsModelValidated(true); // Mark as validated
            } else {
              setStep(4); // StepFive for regular shreds
            }
            break;
          default:
            setStep(0);
            handleErrorMessage("No valid status found in the response. Please try again.");
            break;
        }
        break;
      default:
        setStep(0);
        handleErrorMessage("No valid step found in the response. Please try again.");
        break;
    }
  };
  
  const handleErrorMessage = (message: string) => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    // console.log("scrolling to top")
    
    setErrorMessage(message);
  };
  
  const renderStep = () => {
    console.log("🔍 StepController renderStep:", { step, isModel, shredResponseModel: shredResponse?.body?.model });
    
    switch (step) {
      case 0:
        console.log("🔍 Rendering StepOne");
        return <StepOne onStepValidationResponse={handleValidationResponse} onStepError={handleErrorMessage}
                        setShredName={setShredName}
                        serviceId={serviceId} />;
      case 1:
        console.log("🔍 Rendering StepTwo with model:", shredResponse?.body?.model || isModel);
        console.log("🔍 StepTwo isModelValidated:", isModelValidated);
        return <StepTwo onStepValidationResponse={handleValidationResponse} onStepError={handleErrorMessage}
                        shredId={shredId} shredName={shredName} shredDescription={shredDescription}
                        model={shredResponse?.body?.model || isModel}
                        isAlreadyValidated={isModelValidated}
                        serviceId={serviceId} />;
      case 2:
        console.log("🔍 Rendering StepThree");
        return <StepThree onStepValidationResponse={handleValidationResponse} onStepError={handleErrorMessage}
                          shredId={shredId} shredName={shredName} shredDescription={shredDescription} />;
      case 3:
        console.log("🔍 Rendering StepFour");
        return <StepFour onStepValidationResponse={handleValidationResponse} onStepError={handleErrorMessage}
                         shredId={shredId} shredName={shredName} shredDescription={shredDescription}
                         shredPlan={shredPlan} />;
      case 4:
        console.log("🔍 Rendering Step 4 - isModel:", isModel);
        if (isModel) {
          console.log("🔍 Rendering StepFiveModel");
          return <StepFiveModel shredId={shredId} shredName={shredName} shredDescription={shredDescription} />;
        } else {
          console.log("🔍 Rendering StepFive");
          return <StepFive shredId={shredId} shredName={shredName} shredDescription={shredDescription}
                           shredPlan={shredPlan}
                           shredStartDate={shredStartDate} shredProgress={shredShredProgress}
                           shredCoinsUsed={shredCoinsUsed} shredGenerationTime={shredGenerationTime}
                           onStepError={handleErrorMessage}
                           shredResponse={shredResponse} />;
        }
      default:
        console.log("🔍 Rendering default (null) for step:", step);
        return null;
    }
  };
  
  const renderHints = () => {
    switch (step) {
      case 0:
        return shredNameHint;
      case 1:
        return shredDescriptionHint;
      case 2:
        return shredListHint;
      case 3:
        return shredPlanHint;
      case 4:
        return "";
      default:
        return "Please follow the steps to create your shred.";
    }
  };
  
  useEffect(() => {
    console.log("🔍 StepController useEffect - id:", id);
    if (!id) return;
    getShred();
  }, [getShred]);
  
  // Don't render steps until we have loaded the initial data
  if (loading) {
    return (
      <DashboardLayout>
        <Card>
          <div>
            <StepZero />
          </div>
        </Card>
      </DashboardLayout>
    );
  }
  
  console.log("🔍 StepController main render:", { step, loading, errorMessage, isModel });
  
  return (
    <DashboardLayout>
      <Card>
        {errorMessage && (
          <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 w-full max-w-2xl px-4">
            <Alert
              showIcon
              className="shadow-lg border border-red-200"
              type="danger"
              closable
              duration={0}
              onClose={() => setErrorMessage("")}
            >
              {errorMessage}
            </Alert>
          </div>
        )}
        <div className="m-3 md:m-5">
          {step != 4 && (
            <div className="col-span-12 mb-4 md:mb-6">
              <ExplanationBox
                title="HINT"
                content={renderHints()}
                boxWidth="md:w-full"
                boxHeight="h-full"
                canToggle
              />
            </div>
          )}
          
          {!isModel && (
            <div className="block md:hidden mb-6">
              <Steps current={step} vertical>
                <Steps.Item title="Create Shred" />
                <Steps.Item title="Description Validation" />
                <Steps.Item title="Inputs/Outputs" />
                <Steps.Item title="Plan" />
                <Steps.Item title="Code" />
              </Steps>
            </div>
          )}
          
          {!isModel && (
            <div className="hidden md:block">
              <Steps current={step}>
                <Steps.Item title="Create Shred" />
                <Steps.Item title="Description Validation" />
                <Steps.Item title="Inputs/Outputs" />
                <Steps.Item title="Plan" />
                <Steps.Item title="Code" />
              </Steps>
            </div>
          )}
          
          {isModel && (
            <div className="block md:hidden mb-6">
              <Steps current={step} vertical>
                <Steps.Item title="Create Shred" />
                <Steps.Item title="Description Validation" />
                <Steps.Item title="Model Preview" />
              </Steps>
            </div>
          )}
          
          {isModel && (
            <div className="hidden md:block">
              <Steps current={step}>
                <Steps.Item title="Create Shred" />
                <Steps.Item title="Description Validation" />
                <Steps.Item title="Model Preview" />
              </Steps>
            </div>
          )}
          
          <div className="py-4 md:py-8">
            {renderStep()}
          </div>
        </div>
      </Card>
    </DashboardLayout>
  );
};

export default StepController;